import { supabase } from './supabase';
import { getCache, setCache, clearCache } from '@/lib/utils';
import { Entity } from './entities';

// Cache keys
const PLOTCARD_ENTITIES_CACHE_PREFIX = 'plotcard_entities_';
const CHAPTER_ENTITIES_CACHE_PREFIX = 'chapter_entities_';

// Cache expiration times (in milliseconds)
const CACHE_SHORT = 2 * 60 * 1000; // 2 minutes
const CACHE_MEDIUM = 5 * 60 * 1000; // 5 minutes

/**
 * Interface for plotcard entity relationship
 */
export interface PlotcardEntity {
  id: number;
  plotcard_id: number;
  entity_id: number;
  created_at: string;
  entity?: Entity;
}

/**
 * Interface for chapter entity with template and category info
 */
export interface ChapterEntity {
  entity_id: number;
  entity_name: string;
  entity_description: string | null;
  template_id: number;
  template_name: string;
  category_id: number;
  category_name: string;
}

/**
 * Get entities attached to a plotcard
 * @param plotcardId - Plotcard ID
 * @returns Promise with array of entities attached to the plotcard
 */
export const getPlotcardEntities = async (plotcardId: string | number): Promise<Entity[]> => {
  // Try to get from cache first
  const cacheKey = `${PLOTCARD_ENTITIES_CACHE_PREFIX}${plotcardId}`;
  const cachedEntities = getCache<Entity[]>(cacheKey);

  if (cachedEntities) {
    return cachedEntities;
  }

  // If not in cache, fetch from API
  const { data, error } = await supabase
    .from('plotcard_entities')
    .select(`
      *,
      entity:entity_id (
        *,
        templates:template_id (
          name
        )
      )
    `)
    .eq('plotcard_id', plotcardId);

  if (error) {
    console.error('Error fetching plotcard entities:', error);
    throw error;
  }

  // Extract the entities from the response
  const entities = data.map(item => item.entity) as Entity[];

  // Cache the result
  setCache(cacheKey, entities, CACHE_MEDIUM);

  return entities;
};

/**
 * Add an entity to a plotcard
 * @param plotcardId - Plotcard ID
 * @param entityId - Entity ID
 * @returns Promise with the created plotcard entity relationship
 */
export const addEntityToPlotcard = async (
  plotcardId: string | number,
  entityId: string | number
): Promise<PlotcardEntity> => {
  // The tenant_id will be set by the database trigger
  // We don't need to explicitly set it here
  const { data, error } = await supabase
    .from('plotcard_entities')
    .insert([
      {
        plotcard_id: plotcardId,
        entity_id: entityId
      }
    ])
    .select()
    .single();

  if (error) {
    // If the error is a duplicate key violation, the entity is already attached
    if (error.code === '23505') {
      console.log('Entity already attached to plotcard');

      // Return the existing relationship
      const { data: existingData, error: fetchError } = await supabase
        .from('plotcard_entities')
        .select()
        .eq('plotcard_id', plotcardId)
        .eq('entity_id', entityId)
        .single();

      if (fetchError) {
        console.error('Error fetching existing plotcard entity:', fetchError);
        throw fetchError;
      }

      return existingData;
    }

    console.error('Error adding entity to plotcard:', error);
    throw error;
  }

  // Clear cache
  clearCache(`${PLOTCARD_ENTITIES_CACHE_PREFIX}${plotcardId}`);

  // Also clear any chapter entities cache that might include this plotcard
  // We don't know the chapter ID here, so we can't clear specific cache
  // This will be handled by cache expiration

  return data;
};

/**
 * Remove an entity from a plotcard
 * @param plotcardId - Plotcard ID
 * @param entityId - Entity ID
 * @returns Promise with void
 */
export const removeEntityFromPlotcard = async (
  plotcardId: string | number,
  entityId: string | number
): Promise<void> => {
  const { error } = await supabase
    .from('plotcard_entities')
    .delete()
    .eq('plotcard_id', plotcardId)
    .eq('entity_id', entityId);

  if (error) {
    console.error('Error removing entity from plotcard:', error);
    throw error;
  }

  // Clear cache
  clearCache(`${PLOTCARD_ENTITIES_CACHE_PREFIX}${plotcardId}`);

  // Also clear any chapter entities cache that might include this plotcard
  // We don't know the chapter ID here, so we can't clear specific cache
  // This will be handled by cache expiration
};

/**
 * Get all entities attached to plotcards in a chapter
 * @param chapterId - Chapter ID
 * @param skipCache - Whether to skip the cache and force a fresh fetch
 * @returns Promise with array of entities in the chapter
 */
export const getChapterEntities = async (
  chapterId: string | number,
  skipCache: boolean = false
): Promise<ChapterEntity[]> => {
  const cacheKey = `${CHAPTER_ENTITIES_CACHE_PREFIX}${chapterId}`;

  // Always clear the cache for this chapter to ensure fresh data
  clearCache(cacheKey);
  console.log(`Skipping cache for chapter ${chapterId}`);

  // Fetch from API using the database function
  console.log(`Fetching fresh entities for chapter ${chapterId}`);
  const { data, error } = await supabase
    .rpc('get_chapter_entities_by_category', {
      p_chapter_id: typeof chapterId === 'string' ? parseInt(chapterId) : chapterId
    });

  if (error) {
    console.error('Error fetching chapter entities:', error);
    throw error;
  }

  // Cache the result with a very short expiration time
  // This helps with rapid navigation between pages but ensures fresh data
  setCache(cacheKey, data, CACHE_SHORT / 2); // Use half the normal cache time

  return data;
};
